/**
 * Message Attachment Component
 * Displays file attachments in chat messages
 */

import React, { useState } from 'react';
import { 
  Download, 
  Eye, 
  FileText, 
  Image as ImageIcon, 
  Music, 
  File,
  X,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import messageFileService from '../services/messageFileService';

/**
 * File Preview Modal Component
 */
const FilePreviewModal = ({ file, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  const isImage = file.type.startsWith('image/');
  const isPDF = file.type === 'application/pdf';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl max-h-full w-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center">
            <FileText className="w-5 h-5 mr-2 text-gray-500" />
            <div>
              <h3 className="font-medium text-gray-900 truncate">{file.name}</h3>
              <p className="text-sm text-gray-500">
                {messageFileService.formatFileSize(file.size)} • {file.type}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <a
              href={file.url}
              download={file.name}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
              title="Download"
            >
              <Download size={20} />
            </a>
            
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
              title="Open in new tab"
            >
              <ExternalLink size={20} />
            </a>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mb-2" />
              <p className="text-gray-600">Unable to preview this file</p>
              <a
                href={file.url}
                download={file.name}
                className="mt-2 text-blue-500 hover:text-blue-600"
              >
                Download to view
              </a>
            </div>
          ) : isImage ? (
            <div className="flex justify-center">
              <img
                src={file.url}
                alt={file.name}
                className="max-w-full max-h-full object-contain"
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError(true);
                }}
              />
            </div>
          ) : isPDF ? (
            <iframe
              src={file.url}
              title={file.name}
              className="w-full h-96"
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setError(true);
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <File className="w-12 h-12 text-gray-400 mb-2" />
              <p className="text-gray-600 mb-2">Preview not available</p>
              <a
                href={file.url}
                download={file.name}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Download File
              </a>
            </div>
          )}
          
          {loading && !error && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Message Attachment Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.attachment - File attachment object
 * @param {boolean} props.isCurrentUser - Whether message is from current user
 * @param {boolean} props.compact - Whether to show compact view
 * @returns {JSX.Element} Message attachment component
 */
const MessageAttachment = ({ attachment, isCurrentUser = false, compact = false }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  if (!attachment) return null;
  
  const { name, size, type, url, fileType } = attachment;
  const isImage = type.startsWith('image/');
  const category = messageFileService.getFileCategory(type);
  const formattedSize = messageFileService.formatFileSize(size);
  
  // Get appropriate icon
  const getIcon = () => {
    switch (category) {
      case 'image':
        return <ImageIcon size={compact ? 16 : 20} />;
      case 'audio':
        return <Music size={compact ? 16 : 20} />;
      case 'document':
        if (type === 'application/pdf') {
          return <FileText size={compact ? 16 : 20} />;
        }
        return <FileText size={compact ? 16 : 20} />;
      default:
        return <File size={compact ? 16 : 20} />;
    }
  };
  
  // Handle download
  const handleDownload = (e) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Handle preview
  const handlePreview = (e) => {
    e.stopPropagation();
    if (isImage || type === 'application/pdf') {
      setShowPreview(true);
    } else {
      handleDownload(e);
    }
  };
  
  if (compact) {
    return (
      <>
        <div
          onClick={handlePreview}
          className={`inline-flex items-center px-2 py-1 rounded-md text-xs cursor-pointer transition-colors ${
            isCurrentUser
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {getIcon()}
          <span className="ml-1 truncate max-w-24">{name}</span>
        </div>
        
        {showPreview && (
          <FilePreviewModal
            file={attachment}
            onClose={() => setShowPreview(false)}
          />
        )}
      </>
    );
  }
  
  // Full attachment view
  if (isImage && !imageError) {
    return (
      <>
        <div className="mt-2">
          <div
            onClick={handlePreview}
            className="relative rounded-lg overflow-hidden cursor-pointer max-w-sm"
          >
            <img
              src={url}
              alt={name}
              className={`w-full h-auto max-h-64 object-cover transition-opacity ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true);
                setImageLoaded(true);
              }}
            />
            
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-gray-400" />
              </div>
            )}
            
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
              <div className="bg-white bg-opacity-90 rounded-full p-2">
                <Eye size={20} className="text-gray-700" />
              </div>
            </div>
          </div>
          
          <div className="mt-1 text-xs text-gray-500">
            {name} • {formattedSize}
          </div>
        </div>
        
        {showPreview && (
          <FilePreviewModal
            file={attachment}
            onClose={() => setShowPreview(false)}
          />
        )}
      </>
    );
  }
  
  // Document/file attachment
  return (
    <>
      <div
        onClick={handlePreview}
        className={`mt-2 border rounded-lg p-3 cursor-pointer transition-colors max-w-sm ${
          isCurrentUser
            ? 'border-blue-300 bg-blue-50 hover:bg-blue-100'
            : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <div className="flex items-center">
          <div className={`flex-shrink-0 p-2 rounded ${
            isCurrentUser ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
          }`}>
            {getIcon()}
          </div>
          
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {name}
            </p>
            <p className="text-xs text-gray-500">
              {formattedSize} • {type.split('/')[1]?.toUpperCase() || 'File'}
            </p>
          </div>
          
          <div className="ml-2 flex-shrink-0">
            <button
              onClick={handleDownload}
              className={`p-1 rounded transition-colors ${
                isCurrentUser
                  ? 'text-blue-600 hover:bg-blue-200'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
              title="Download"
            >
              <Download size={16} />
            </button>
          </div>
        </div>
      </div>
      
      {showPreview && (
        <FilePreviewModal
          file={attachment}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};

export default MessageAttachment;